import React from 'react';
import LegalLayout from './LegalLayout';

// Public documentation for the Gain Rhino CRM connector for Claude
// (hosted MCP server at /api/mcp). Linked from the OAuth metadata
// (service_documentation), the consent page and the Connectors Directory
// listing, so it must stay reachable without a login.

const MCP_URL = 'https://crm.gainrhino.com/api/mcp';

const TOOLS = [
  ['Search leads', 'search_leads', 'Read', 'Find leads by name, email, phone or company.'],
  ['Get lead details', 'get_lead', 'Read', 'Contact info, stage, owner, tags and recent note previews.'],
  ['Read lead notes', 'get_lead_notes', 'Read', 'Full timeline text (notes, emails, SMS, call summaries), paginated.'],
  ['List pipeline stages', 'list_stages', 'Read', 'Pipelines and stage ids.'],
  ['List tasks', 'list_tasks', 'Read', 'Open and completed tasks, paginated.'],
  ['Run sales report', 'run_report', 'Read', 'Funnel, win rate, revenue and per-user performance.'],
  ['Create lead', 'create_lead', 'Write', 'Adds a lead; duplicates by email or phone are rejected.'],
  ['Update lead', 'update_lead', 'Write', 'Overwrites contact or deal fields.'],
  ['Move lead to stage', 'move_lead_stage', 'Write', 'Changes the pipeline stage; may trigger your automations and integrations.'],
  ['Add note to lead', 'add_note', 'Write', 'Internal note on the lead timeline.'],
  ['Attach file from URL', 'add_attachment_from_url', 'Write', 'Downloads a public file (max 10 MB) onto the lead.'],
  ['Create PDF document for lead', 'create_lead_document', 'Write', 'Renders a branded PDF and attaches it.'],
  ['Create task', 'create_task', 'Write', 'Follow-up task for you, optionally linked to a lead.'],
  ['Complete task', 'complete_task', 'Write', 'Marks a task done.'],
  ['Send email to lead', 'send_email', 'Sends', 'Real email from your organization\'s mailbox.'],
  ['Send SMS to lead', 'send_sms', 'Sends', 'Real SMS through your phone provider.'],
  ['Get AI phone receptionist', 'get_phone_agent', 'Read', 'Phone agent configuration and status (admins, Phone Agent add-on).'],
  ['List owned phone numbers', 'list_owned_agent_numbers', 'Read', 'Numbers you already own.'],
  ['Analyze business website', 'analyze_agent_website', 'Write', 'Drafts a business profile and FAQs from your site; nothing goes live.'],
  ['Update AI phone receptionist', 'update_phone_agent', 'Write', 'Changes greeting, voice, transfer rules and more.'],
  ['Confirm phone agent knowledge', 'confirm_agent_knowledge', 'Write', 'Saves the reviewed profile and FAQs.'],
  ['Activate AI phone receptionist', 'activate_phone_agent', 'Write', 'Goes live with a number you already own, or republishes changes. New numbers are bought in the CRM app, never through Claude.'],
];

const H2 = ({ children, id }) => <h2 id={id} className="text-xl font-semibold text-gray-900 pt-6">{children}</h2>;

const ClaudeIntegration = () => (
  <LegalLayout title="Gain Rhino CRM for Claude" lastUpdated="2026-10-14">
    <p>
      The Gain Rhino CRM connector lets Claude work with your CRM: look up leads and their history,
      summarize conversations, keep your pipeline and tasks up to date, draft and send follow-ups,
      and set up the AI phone receptionist. Claude always acts as you, with your role's permissions,
      inside your own organization.
    </p>

    <H2 id="setup">Connect</H2>
    <p>You need a Gain Rhino CRM account. The connection uses your normal CRM login (OAuth); no API keys to copy.</p>
    <p><strong>claude.ai and Claude Desktop</strong></p>
    <ol className="list-decimal pl-6 space-y-1">
      <li>Open <strong>Settings → Connectors</strong>. Pick <strong>Gain Rhino CRM</strong> from the directory, or choose <strong>Add custom connector</strong> and enter <code className="bg-gray-100 px-1 rounded break-all">{MCP_URL}</code>.</li>
      <li>Click <strong>Connect</strong>. Sign in to the CRM if asked, review what Claude will be able to do, and click <strong>Allow</strong>.</li>
      <li>In a chat, enable the connector from the tools menu and ask away.</li>
    </ol>
    <p><strong>Claude Code</strong></p>
    <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{`claude mcp add --transport http gain-rhino-crm ${MCP_URL}`}</pre>
    <p>Then run <code className="bg-gray-100 px-1 rounded">/mcp</code> inside Claude Code and choose Authenticate.</p>

    <H2 id="examples">Things to ask</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li>"Which new leads came in since yesterday, and who hasn't been contacted yet?"</li>
      <li>"Summarize the conversation with Samuel Osei and create a follow-up task for Friday."</li>
      <li>"Move Emily Novak to Treatment Plan Sent and add a note that financing was discussed."</li>
      <li>"How did we do this month? Show win rate and revenue by stage."</li>
      <li>"Draft a short SMS to Grace Liu about the whitening special and show it to me before sending."</li>
    </ul>

    <H2 id="tools">Tools</H2>
    <p>
      Tools are marked for Claude as read-only, write, or destructive/external, so Claude asks for your
      permission before anything that changes data or contacts a customer (unless you have chosen to
      always allow a tool). Claude cannot spend money in your account. Tools your role cannot use are
      not offered at all.
    </p>
    <div className="overflow-x-auto -mx-2">
      <table className="min-w-full text-xs border border-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="text-left p-2 border-b">Tool</th>
            <th className="text-left p-2 border-b">Type</th>
            <th className="text-left p-2 border-b">What it does</th>
          </tr>
        </thead>
        <tbody>
          {TOOLS.map(([title, name, kind, what]) => (
            <tr key={name} className="align-top">
              <td className="p-2 border-b">
                <div className="font-medium text-gray-800">{title}</div>
                <code className="text-gray-500">{name}</code>
              </td>
              <td className="p-2 border-b whitespace-nowrap">{kind}</td>
              <td className="p-2 border-b">{what}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <H2 id="security">Security and permissions</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li>OAuth 2.1 with PKCE. Claude never sees your password. Access tokens last one hour and are refreshed automatically; we store only hashes of them.</li>
      <li>Claude gets exactly your CRM permissions. Sales reps only reach leads and tasks they can see in the app; other organizations' data is never reachable.</li>
      <li>Every request is written to the CRM audit trail, like normal app usage.</li>
      <li>Usage limits protect your account: for example at most 30 emails and 30 SMS per hour per user through Claude, and 5 phone agent activations per day per organization.</li>
      <li>Disconnect any time under <strong>Settings → Connect Claude → Connected apps</strong>, or remove the connector in Claude. Changing your CRM password also signs every connection out.</li>
    </ul>

    <H2 id="data">Your data</H2>
    <p>
      CRM data is sent to Claude only when Claude calls a tool for you, and only what that tool returns.
      Lead records can contain personal and, for dental and medical practices, health information. Once
      delivered, Anthropic processes it under your agreement with Anthropic. If your organization handles
      protected health information, use a Claude plan covered by a Business Associate Agreement before
      connecting. Text in CRM records is written by customers and other people; Claude is told to treat it
      as data, never as instructions. See our <a href="/privacy" className="text-primary-600 hover:underline">Privacy Policy</a>.
    </p>

    <H2 id="troubleshooting">Troubleshooting</H2>
    <ul className="list-disc pl-6 space-y-1">
      <li><strong>"Not found" for a lead you can see by name:</strong> ask Claude to search for it first; tools need the lead id.</li>
      <li><strong>Phone agent tools say "Not found":</strong> the Phone Agent add-on is not enabled for your organization, or your role is not an admin.</li>
      <li><strong>Claude says the connection is no longer valid:</strong> reconnect from Claude's connector settings (this happens after a password change or a disconnect).</li>
      <li><strong>Rate limit messages:</strong> wait for the time shown, or send larger batches from the CRM itself.</li>
    </ul>

    <H2 id="support">Support</H2>
    <p>
      Email <a href="mailto:info@gainrhino.com" className="text-primary-600 hover:underline">info@gainrhino.com</a>.
      We reply within 5 business days, usually much sooner.
    </p>
  </LegalLayout>
);

export default ClaudeIntegration;
